// Barra compatta: controlli Spotify se connesso + playback attivo (Premium consigliato)
import { useEffect, useState, useCallback } from 'react'
import { SkipBack, SkipForward } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { SpotifyMark } from './BrandMarks'

export default function SpotifyRemoteBar() {
  const location = useLocation()
  const [snap, setSnap] = useState(null)
  const [busy, setBusy] = useState(false)
  const workoutRoutes = ['/forza', '/resistenza', '/hyrox', '/piano/sessione']
  const onWorkoutRoute = workoutRoutes.includes(location.pathname)

  const poll = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setSnap(null)
        return
      }
      const st = await fetch('/api/spotify/status', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      }).then(r => r.json()).catch(() => ({}))
      if (!st?.connected) {
        setSnap({ mode: 'off' })
        return
      }
      const ctrl = await fetch('/api/spotify/control', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      }).then(r => r.json()).catch(() => ({}))

      if (!ctrl?.connected || ctrl.reason === 'not_connected') {
        setSnap({ mode: 'off' })
        return
      }
      if (ctrl.restricted) {
        setSnap({ mode: 'restricted' })
        return
      }
      if (ctrl.idle || ctrl.error) {
        setSnap({ mode: 'idle' })
        return
      }
      setSnap({
        mode: 'active',
        name: ctrl.name,
        artists: ctrl.artists,
        playing: ctrl.is_playing,
      })
    } catch {
      setSnap(null)
    }
  }, [])

  useEffect(() => {
    if (location.pathname === '/auth' || !onWorkoutRoute) {
      setSnap(null)
      return
    }
    poll()
    const id = setInterval(poll, 14000)
    return () => clearInterval(id)
  }, [location.pathname, onWorkoutRoute, poll])

  async function send(action) {
    setBusy(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return
      await fetch('/api/spotify/control', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      })
      setTimeout(poll, 400)
    } catch (_) {}
    setBusy(false)
  }

  if (!onWorkoutRoute || !snap || snap.mode === 'off' || snap.mode === 'idle') return null

  if (snap.mode === 'restricted') {
    return (
      <div
        style={{
          position: 'fixed',
          left: 12,
          right: 12,
          bottom: 'calc(132px + env(safe-area-inset-bottom))',
          zIndex: 95,
        }}
      >
        <div style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'rgba(36,35,37,0.63)',
          textAlign: 'center',
          padding: '8px 12px',
          borderRadius: 12,
          background: 'rgba(255,255,255,0.92)',
          border: '1px solid rgba(36,35,37,0.07)',
        }}
        >
          Spotify: controlli remoti richiedono account Premium
        </div>
      </div>
    )
  }

  const bottom = 'calc(132px + env(safe-area-inset-bottom))'

  return (
    <div
      style={{
        position: 'fixed',
        left: 10,
        right: 10,
        bottom,
        zIndex: 95,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          pointerEvents: 'all',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '9px 12px',
          borderRadius: 14,
          background: 'rgba(255,255,255,0.94)',
          border: '1px solid rgba(36,35,37,0.10)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(36,35,37,0.14)',
        }}
      >
        <SpotifyMark size={18} color="#1DB954" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          {snap.mode === 'idle' ? (
            <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(36,35,37,0.63)' }}>
              Spotify connesso — avvia riproduzione nell’app e torna qui
            </div>
          ) : (
            <>
              <div style={{
                fontSize: 11, fontWeight: 800, color: '#242325', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
              >
                {snap.playing !== false ? '▶︎ ' : ''}{snap.name || '—'}
              </div>
              {snap.artists ? (
                <div style={{
                  fontSize: 10, fontWeight: 600, color: 'rgba(36,35,37,0.63)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
                >
                  {snap.artists}
                </div>
              ) : null}
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button
            type="button"
            aria-label="Traccia precedente"
            disabled={busy}
            onClick={() => send('previous')}
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              border: '1px solid rgba(36,35,37,0.12)',
              background: 'rgba(36,35,37,0.06)',
              color: '#242325',
              cursor: busy ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <SkipBack size={16} strokeWidth={2.2} />
          </button>
          <button
            type="button"
            aria-label="Traccia successiva"
            disabled={busy}
            onClick={() => send('next')}
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              border: '1px solid rgba(29,185,84,0.35)',
              background: 'rgba(29,185,84,0.12)',
              color: '#1DB954',
              cursor: busy ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <SkipForward size={16} strokeWidth={2.2} />
          </button>
        </div>
      </div>
    </div>
  )
}
